import React from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Divider, Row } from '@firsttable/web-components/atoms';
import HomeInfo from '@firsttable/web-components/organisms/HomeInfo';
import {
  countFormat,
  currencyFormat,
  roundToClosest,
} from '@firsttable/functions';
import WhyBox from '@firsttable/web-components/molecules/WhyBox';
import Reviews from '@firsttable/web-components/organisms/Swiper';
import { space } from '@firsttable/web-theme';
import { useLocation } from '@reach/router';
import queryString from 'query-string';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { graphql, useStaticQuery } from 'gatsby';
import Footer from '../components/organisms/Footer';
import HomeHero from '../components/organisms/HomeHero';
import Why1 from '../images/why1.svg';
import Why2 from '../images/why2.svg';
import Why3 from '../images/what-we-stand-for.svg';
import ellipse from '../images/ellipse-01.svg';
import JoinFirstTable from '../components/organisms/JoinFirstTable';
import HomeCities from '../components/organisms/HomeCities';
import ThemeLayout from './layout';
import RestaurantOthers from '../components/organisms/RestaurantOther';
import NavLinkComponent from '../components/atoms/NavLink';
import SEO from '../components/seo';

const GET_PROMO_RESTAURANTS = gql`
  query getPromoRestaurants($cityId: Int!) {
    region: City(id: $cityId) {
      slug
      menuTitle
    }
    restaurants: paginatedRestaurants(regionId: $cityId, limit: 10) {
      edges {
        node {
          id
          cityId
          title
          slug
          availabilitySessions
          cuisines {
            edges {
              node {
                id: id
                label: title
              }
            }
          }
          allImages: images(limit: 1) {
            edges {
              node {
                id
                url
              }
            }
          }
        }
      }
    }
  }
`;

const PromoRestaurants = () => {
  const { search } = useLocation();
  const { City } = queryString.parse(search);
  if (!City) {
    return null;
  }
  const { data, error, loading } = useQuery(GET_PROMO_RESTAURANTS, {
    variables: { cityId: +City },
  });
  if (error || loading) {
    return null;
  }
  return (
    <>
      <RestaurantOthers
        NavComponent={NavLinkComponent}
        title={`Experience ${data?.region?.menuTitle} restaurants`}
        restaurants={data.restaurants}
        availabilitySession="dinner"
        region={data.region}
        mb={[null, 30]}
      />
      <Container>
        <Row>
          <Col>
            <Divider mb="40px" />
          </Col>
        </Row>
      </Container>
    </>
  );
};

const PromoLayout = ({ city, ...props }) => {
  const { regions, content, siteConfig, homeReviews } = useStaticQuery(graphql`
    query promoLayoutContent {
      regions: allCity(filter: { parentId: { eq: 0 } }) {
        edges {
          node {
            id
            foreignId
            menuTitle
            slug
            regionId
            parentId
          }
        }
      }
      siteConfig {
        restaurantCount
        currency
        bookingPrice
      }
      content: allWhyContentJson {
        edges {
          node {
            title
            content
            id
          }
        }
      }
      homeReviews: allReview(limit: 7) {
        edges {
          node {
            id
            foreignId
            date
            comment
            name
            serviceRating
            foodRating
            userAvatar
            restaurant {
              title
            }
          }
        }
      }
    }
  `);
  let bookingMessage = `Book a First Table for ${currencyFormat(
    siteConfig.bookingPrice,
    siteConfig.currency,
  )} and get 50% off the food bill for two, three or four people`;
  if (siteConfig.siteId === 12) {
    bookingMessage =
      'Book a First Table for Rp50k or Rp100k and get 50% off the food bill for two, three or four people';
  }
  return (
    <ThemeLayout footer={<Footer />}>
      {city && (
        <SEO
          title={city.metaTitleFormatted}
          id={city.foreignId}
          description={city.metaDescription}
          ogImage={city.ogImage}
          metaTags={city.metaTags}
          slug={city.slug}
        />
      )}
      <HomeHero city={city} regions={regions} {...props} />
      <Container>
        <Row>
          <Col width={[1, 1 / 3]} mb={['35px', 0]}>
            <HomeInfo
              icon="discovery"
              title="Find a restaurant"
              message={`Find a new restaurant to try from over ${countFormat(
                roundToClosest(siteConfig.restaurantCount, 100),
              )} restaurants globally`}
            />
          </Col>
          <Col width={[1, 1 / 3]} mb={['35px', 0]}>
            <HomeInfo
              icon="reviews"
              title="Select your date and time"
              message="Get in quick to score the ‘first table’ of breakfast, lunch or dinner"
            />
          </Col>
          <Col width={[1, 1 / 3]}>
            <HomeInfo
              icon="discount"
              title="Book a First Table"
              message={bookingMessage}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Divider m={['50px 0', '95px 0']} />
          </Col>
        </Row>
      </Container>
      <div>
        <PromoRestaurants />
      </div>
      <WhyBox
        mb={['m', 70]}
        number={1}
        icon={Why1}
        title={content.edges[0].node.title}
        message={content.edges[0].node.content.replace(
          '[restaurants]',
          countFormat(roundToClosest(siteConfig.restaurantCount, 100)),
        )}
      />
      <WhyBox
        mb={['m', 40]}
        number={2}
        icon={Why2}
        title={content.edges[1].node.title}
        textLayout="right"
        message={content.edges[1].node.content.replace(
          '[restaurants]',
          countFormat(roundToClosest(siteConfig.restaurantCount, 100)),
        )}
      />
      <WhyBox
        number={3}
        icon={Why3}
        title={content.edges[2].node.title}
        message={content.edges[2].node.content.replace(
          '[restaurants]',
          countFormat(roundToClosest(siteConfig.restaurantCount, 100)),
        )}
      />
      <Container>
        <Divider my={[50, 100]} />
      </Container>
      {homeReviews.edges.length > 0 && <Reviews reviews={homeReviews.edges} />}
      <Container
        mb={['50px', '50px', 0]}
        background={[null, null, `url(${ellipse}) no-repeat 0 50%`]}
        pt={[null, null, '155px']}
        pb={[null, null, '175px']}
        pl={[space.s, space.m, '135px']}
      >
        <JoinFirstTable />
      </Container>
      <HomeCities regions={regions} />
    </ThemeLayout>
  );
};

PromoLayout.propTypes = {
  city: PropTypes.shape(),
};
PromoLayout.defaultProps = {};

export default PromoLayout;
